import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { getVariantMapFor, money } from '@theme/jhilke-buybox';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * EG 2026-10-07: Add to cart answers at once (Amrit chose the fully optimistic drawer).
 *
 * Product page and quick-add adds dispatch Shopify's cart lines-update event synchronously, before the
 * cart/add request leaves (assets/product-form.js). This listener runs first (capture phase) and:
 *   1. raises the cart count,
 *   2. draws the new line in the drawer from <template id="jh-optimistic-line"> in
 *      snippets/cart-drawer.liquid (the cart line's own classes, filled from the variant map), or raises
 *      the quantity of the line that already holds that variant, and shimmers the total,
 *   3. for an empty cart, swaps the empty message for a small cart shell so the drawer opens now
 *      (assets/cart-drawer.js only opens at once when the cart is not empty).
 * Shopify's answer then re-renders the drawer (component-cart-items.js), which replaces everything drawn
 * here. A refused add (422) or a failed request puts everything back, closes the drawer this add opened,
 * and leaves the message by the button (product-form.js).
 *
 * Text goes in with textContent; nothing from the map passes through innerHTML.
 *
 * ET 2026-10-09: the same instant answer for the numbers.
 *   - An add paints the new drawer total, the free-shipping line and (for a variant already in the cart) the
 *     line price at the click, from the cart Shopify last sent (data-jh-cart on .cart-drawer__inner,
 *     snippets/cart-drawer.liquid).
 *   - A + or - tap in the drawer paints the line price, total, free-shipping line and cart count in the same
 *     frame as the tap. Horizon's debounce, request and morph run as before and replace every number.
 *     Only paint: no price from the page is ever sent to Shopify, there is no new request, and a refused
 *     change puts the old numbers back and re-reads the drawer from Shopify.
 */

const DRAWER_SECTION_ID = 'cart-drawer-section';

/** @typedef {() => void} Undo */

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate, true);

/**
 * @param {Event} event
 */
function onCartLinesUpdate(event) {
  const cartEvent = /** @type {any} */ (event);
  if (cartEvent.action !== 'add' || cartEvent.context !== 'product') return;

  const lines = cartEvent.lines;
  if (!Array.isArray(lines) || lines.length !== 1) return;

  const source = /** @type {Element} */ (event.target);
  const form = source?.closest?.('product-form-component');
  if (!(form instanceof HTMLElement)) return;

  const map = getVariantMapFor(form, form.dataset.productId);
  // The event may carry the id as a GID ("gid://shopify/ProductVariant/123"): compare the number.
  const variantId = String(lines[0].merchandiseId ?? '').split('/').pop();
  const variant = map?.variants.find((v) => String(v.id) === variantId);
  if (!map || !variant) return;

  const quantity = Math.max(1, Number(lines[0].quantity) || 1);
  const themeDrawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  const inner = themeDrawer?.querySelector('.cart-drawer__inner');
  if (!inner) return;

  const drawerWasOpen = Boolean(themeDrawer.isOpen);
  /** @type {Undo[]} */
  const undo = [];

  try {
    drawLine(inner, map, variant, quantity, undo);
    bumpCount(quantity, undo);
  } catch (error) {
    console.warn('[jhilke-optimistic-cart] could not draw the line, waiting for Shopify', error);
    rollback(undo);
    return;
  }

  const putBack = () => {
    rollback(undo);
    if (!drawerWasOpen && themeDrawer.isOpen) themeDrawer.close?.();
  };

  cartEvent.promise
    ?.then((/** @type {any} */ result) => {
      // Refused (422): component-cart-items.js re-renders the drawer from Shopify anyway.
      if (result?.detail?.didError) putBack();
    })
    .catch(() => {
      // Never reached Shopify: nothing re-renders the drawer, so put it back and re-read the real cart.
      putBack();
      sectionRenderer.renderSection(DRAWER_SECTION_ID, { cache: false, mode: 'hydration' }).catch(() => {});
    });
}

/**
 * @param {Element} inner - .cart-drawer__inner
 * @param {any} map
 * @param {any} variant
 * @param {number} quantity
 * @param {Undo[]} undo
 */
function drawLine(inner, map, variant, quantity, undo) {
  let tbody = inner.querySelector('.cart-items__table tbody');
  const cart = readCart(inner);

  if (!tbody) {
    // Empty cart: hide the empty message and put a small cart in its place.
    const shellTemplate = /** @type {HTMLTemplateElement | null} */ (document.getElementById('jh-optimistic-shell'));
    const emptyContent = inner.querySelector('cart-items-component .cart-drawer__content');
    if (!shellTemplate || !emptyContent) throw new Error('no empty-cart shell');

    const shell = /** @type {HTMLElement} */ (shellTemplate.content.firstElementChild?.cloneNode(true));
    const empty = /** @type {HTMLElement} */ (emptyContent);
    emptyContent.after(shell);
    // Horizon's CSS gives .cart-drawer__content a display value, which beats the hidden attribute.
    const displayBefore = empty.style.display;
    empty.style.display = 'none';
    undo.push(() => {
      shell.remove();
      empty.style.display = displayBefore;
    });

    if (inner.classList.contains('cart-drawer--empty')) {
      inner.classList.remove('cart-drawer--empty');
      undo.push(() => inner.classList.add('cart-drawer--empty'));
    }

    const subtotal = shell.querySelector('[data-jh-shell-total]');
    if (subtotal) subtotal.textContent = money(map, variant.price * quantity);
    tbody = shell.querySelector('tbody');
  }
  if (!tbody) throw new Error('no cart table');

  // The same variant already in the cart: raise that line instead of drawing a second one.
  const existing = tbody.querySelector(`tr[data-key^="${CSS.escape(String(variant.id))}:"]`);
  if (existing) {
    const input = /** @type {HTMLInputElement | null} */ (existing.querySelector('input[name="updates[]"]'));
    if (input) {
      const before = input.value;
      input.value = String((Number.parseInt(before, 10) || 0) + quantity);
      undo.push(() => {
        input.value = before;
      });
    }
    existing.setAttribute('aria-busy', 'true');
    existing.setAttribute('data-optimistic-line', '');
    undo.push(() => {
      existing.removeAttribute('aria-busy');
      existing.removeAttribute('data-optimistic-line');
    });
    const linePrice = existing.querySelector('.cart-items__price text-component');
    const known = cart?.lines[existing.getAttribute('data-key') ?? ''];
    if (cart && known && linePrice) putText(linePrice, formatCents(cart, known[2] * ((known[0] || 0) + quantity)), undo);
    else shimmer(linePrice, undo);
  } else {
    const rowTemplate = /** @type {HTMLTemplateElement | null} */ (document.getElementById('jh-optimistic-line'));
    const row = /** @type {HTMLElement | null} */ (rowTemplate?.content.firstElementChild?.cloneNode(true) ?? null);
    if (!row) throw new Error('no line template');

    const image = /** @type {HTMLImageElement | null} */ (row.querySelector('[data-jh-line-image]'));
    const src = variant.image || map.image;
    if (image && src) image.src = src;
    else image?.closest('.cart-items__media-container')?.remove();

    setText(row, '[data-jh-line-title]', map.title);
    setText(row, '[data-jh-line-variant]', variant.options.filter((value) => value && value !== '-').join(', '));
    setText(row, '[data-jh-line-unit]', money(map, variant.price));
    setText(row, '[data-jh-line-qty]', String(quantity));
    setText(row, '[data-jh-line-price]', money(map, variant.price * quantity));

    // Shopify lists the newest line first.
    tbody.prepend(row);
    undo.push(() => row.remove());
  }

  // Count in the drawer title, the total and the free-shipping line. A discount (a code) can
  // change the total a little: Shopify's answer replaces it. With no cart data the total shimmers instead.
  const badge = inner.querySelector('.theme-drawer__badge');
  if (badge) {
    const before = badge.textContent ?? '';
    const count = Number.parseInt(before, 10);
    if (Number.isFinite(count)) {
      badge.textContent = String(count + quantity);
      undo.push(() => {
        badge.textContent = before;
      });
    }
  }
  const totalElement = inner.querySelector('text-component[ref="cartTotal"]');
  if (cart && totalElement) {
    const total = cart.total + variant.price * quantity;
    putText(totalElement, formatCents(cart, total), undo);
    paintShipping(inner, cart, total, undo);
  } else {
    shimmer(totalElement, undo);
  }
}

/**
 * @param {number} quantity
 * @param {Undo[]} undo
 */
function bumpCount(quantity, undo) {
  // The count shoppers see is the Jhilke header's badge (sections/jhilke-header.liquid). Its own listener
  // writes Shopify's count once the add settles, success or not.
  const badge = document.querySelector('[data-jh-cart-count]');
  if (badge) {
    const before = badge.textContent ?? '';
    const count = (Number.parseInt(before, 10) || 0) + quantity;
    badge.textContent = String(count);
    const link = document.querySelector('[data-jh-cart-link]');
    const label = link?.getAttribute('aria-label') ?? null;
    link?.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    undo.push(() => {
      badge.textContent = before;
      if (link && label !== null) link.setAttribute('aria-label', label);
    });
  }

  // Horizon's own cart icon, where a page still has one.
  for (const element of document.querySelectorAll('cart-icon')) {
    const icon = /** @type {any} */ (element);
    if (typeof icon.renderCartBubble !== 'function') continue;
    const before = Number(icon.currentCartCount) || 0;
    icon.renderCartBubble(before + quantity, true);
    undo.push(() => icon.renderCartBubble(before, false));
  }
}

/**
 * @param {Element | null} element
 * @param {Undo[]} undo
 */
function shimmer(element, undo) {
  if (!element || typeof (/** @type {any} */ (element).shimmer) !== 'function') return;
  /** @type {any} */ (element).shimmer();
  undo.push(() => element.removeAttribute('shimmer'));
}

/**
 * @param {Element} root
 * @param {string} selector
 * @param {string} text
 */
function setText(root, selector, text) {
  const element = root.querySelector(selector);
  if (element) element.textContent = text;
}

/**
 * @param {Undo[]} undo
 */
function rollback(undo) {
  while (undo.length) {
    try {
      undo.pop()?.();
    } catch (error) {
      console.warn('[jhilke-optimistic-cart] rollback step failed', error);
    }
  }
}

/* ET 2026-10-09: + and - in the drawer ------------------------------------------------------------------ */

const FREE_SHIPPING_MESSAGE = 'You have unlocked free shipping';

/** @type {Map<string, number>} line key to the quantity the shopper asked for, until Shopify has answered */
const intent = new Map();
/** @type {Undo[]} */
let paintUndo = [];
let awaitingRequest = false;
let inflight = 0;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let watchdog;

document.addEventListener(ThemeEvents.quantitySelectorUpdate, onQuantityTap, true);
document.addEventListener('click', onQuantityClick, true);
document.addEventListener(StandardEvents.cartLinesUpdate, onDrawerRequest, true);

/**
 * A + or - tap starts from the number the shopper last asked for, not from whatever an older answer put back
 * in the box. Runs before the quantity selector reads its input.
 * @param {Event} event
 */
function onQuantityClick(event) {
  if (!intent.size) return;
  const button = /** @type {Element | null} */ (event.target)?.closest?.(
    '#cart-drawer button[name="plus"], #cart-drawer button[name="minus"]'
  );
  const row = button?.closest('tr[data-key]');
  const key = row?.getAttribute('data-key');
  if (!row || !key || !intent.has(key)) return;
  const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[name="updates[]"]'));
  if (input && input.value !== String(intent.get(key))) input.value = String(intent.get(key));
}

/**
 * @param {Event} event
 */
function onQuantityTap(event) {
  const component = /** @type {Element} */ (event.target)?.closest?.('#cart-drawer cart-items-component');
  const inner = component?.closest('.cart-drawer__inner');
  if (!component || !inner) return;

  const { quantity, cartLine } = /** @type {any} */ (event).detail ?? {};
  // Quantity 0 is a removal: Horizon already empties or drops the row at once.
  if (!cartLine || !Number.isFinite(quantity) || quantity < 1) return;

  const row = component.querySelectorAll('[ref="cartItemRows[]"]')[cartLine - 1];
  const key = row?.getAttribute('data-key');
  if (!key) return;

  intent.set(key, quantity);
  awaitingRequest = true;
  clearTimeout(watchdog);
  // Horizon sends the change 300 ms after the last tap. If nothing leaves, read the drawer back from Shopify.
  watchdog = setTimeout(() => {
    if (inflight === 0 && awaitingRequest) resync(inner);
  }, 1500);
  paintIntent(inner);
}

/**
 * The drawer's own change requests: count them, and settle when the last one has answered.
 * @param {Event} event
 */
function onDrawerRequest(event) {
  const cartEvent = /** @type {any} */ (event);
  if (cartEvent.context !== 'cart' || !cartEvent.promise) return;
  const inner = /** @type {Element} */ (event.target)?.closest?.('#cart-drawer .cart-drawer__inner');
  if (!inner) return;

  const key = cartEvent.lines?.[0]?.id;
  if (cartEvent.action === 'update') awaitingRequest = false;
  else if (key) intent.delete(key);
  inflight += 1;

  cartEvent.promise.then(
    () => {
      inflight -= 1;
      // Horizon has morphed the server's numbers in: they replace what was painted.
      paintUndo = [];
      settle(inner, false);
    },
    () => {
      inflight -= 1;
      // Refused or failed: no morph happened, so put the old numbers back, then read the drawer from Shopify.
      rollback(paintUndo);
      paintUndo = [];
      if (key) intent.delete(key);
      settle(inner, true);
    }
  );
}

/**
 * @param {Element} inner
 * @param {boolean} failed
 */
function settle(inner, failed) {
  if (inflight === 0 && !awaitingRequest) {
    clearTimeout(watchdog);
    intent.clear();
    for (const element of inner.querySelectorAll('[data-jh-painted]')) element.removeAttribute('data-jh-painted');
    setBusy(inner, false);
    if (failed) sectionRenderer.renderSection(DRAWER_SECTION_ID, { cache: false, mode: 'hydration' }).catch(() => {});
    return;
  }
  // A later tap is still on its way: Horizon's morph carried the older answer, paint the shopper's number again.
  setTimeout(() => paintIntent(inner), 60);
}

/**
 * @param {Element} inner
 */
function resync(inner) {
  clearTimeout(watchdog);
  rollback(paintUndo);
  paintUndo = [];
  intent.clear();
  awaitingRequest = false;
  setBusy(inner, false);
  sectionRenderer.renderSection(DRAWER_SECTION_ID, { cache: false, mode: 'hydration' }).catch(() => {});
}

/**
 * Paint every line the shopper changed, then the total, the free-shipping line and the count, from the cart
 * Shopify last sent plus what the shopper asked for.
 * @param {Element} inner
 */
function paintIntent(inner) {
  const cart = readCart(inner);
  if (!cart) return;

  rollback(paintUndo);
  /** @type {Undo[]} */
  const undo = (paintUndo = []);

  let total = cart.total;
  let count = cart.count;
  for (const [key, quantity] of intent) {
    const known = cart.lines[key];
    const row = inner.querySelector(`tr[data-key="${CSS.escape(key)}"]`);
    if (!known || !row) continue;

    const line = known[2] * quantity;
    total += line - known[1];
    count += quantity - known[0];

    const price = row.querySelector('.cart-items__price text-component');
    if (price) putText(price, formatCents(cart, line), undo);
    // A slower, older answer may have put the earlier number back in the box: keep the shopper's.
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[name="updates[]"]'));
    if (input && input.value !== String(quantity)) input.value = String(quantity);
  }

  const totalElement = inner.querySelector('text-component[ref="cartTotal"]');
  if (totalElement) putText(totalElement, formatCents(cart, total), undo);
  paintShipping(inner, cart, total, undo);
  setBusy(inner, true, undo);

  const shown = Number.parseInt(document.querySelector('[data-jh-cart-count]')?.textContent ?? '', 10);
  const delta = count - (Number.isFinite(shown) ? shown : cart.count);
  if (delta !== 0) bumpCount(delta, undo);
  const badge = inner.querySelector('.theme-drawer__badge');
  if (badge && Number.isFinite(Number.parseInt(badge.textContent ?? '', 10))) {
    const before = badge.textContent ?? '';
    badge.textContent = String(count);
    undo.push(() => {
      badge.textContent = before;
    });
  }
}

/**
 * What the drawer's last server render said: totals in cents, per-line [quantity, line price, unit price].
 * @param {Element} inner
 * @returns {{ total: number, count: number, free: number, lines: Record<string, number[]>, format: string, currency: string } | null}
 */
function readCart(inner) {
  try {
    const data = JSON.parse(/** @type {HTMLElement} */ (inner).dataset.jhCart ?? '');
    const { moneyFormat, currency } = /** @type {HTMLElement} */ (inner).dataset;
    if (![data?.total, data?.count, data?.free].every(Number.isFinite) || !moneyFormat) return null;
    return { total: data.total, count: data.count, free: data.free, lines: data.lines ?? {}, format: moneyFormat, currency: currency ?? '' };
  } catch {
    return null;
  }
}

/**
 * @param {{ format: string, currency: string }} cart
 * @param {number} cents
 */
function formatCents(cart, cents) {
  return formatMoney(Math.max(0, Math.round(cents)), cart.format, cart.currency);
}

/**
 * Put a price in a text-component (or any element) and keep the shimmer off it until Shopify answers.
 * @param {Element} element
 * @param {string} text
 * @param {Undo[]} undo
 */
function putText(element, text, undo) {
  const before = {
    text: element.textContent ?? '',
    value: element.getAttribute('value'),
    shimmer: element.hasAttribute('shimmer'),
    painted: element.hasAttribute('data-jh-painted'),
  };
  element.textContent = text;
  // text-component's shimmer overlay draws attr(value).
  if (before.value !== null) element.setAttribute('value', text);
  element.setAttribute('data-jh-painted', '');
  undo.push(() => {
    element.textContent = before.text;
    if (before.value !== null) element.setAttribute('value', before.value);
    element.toggleAttribute('shimmer', before.shimmer);
    element.toggleAttribute('data-jh-painted', before.painted);
  });
}

/**
 * The free-shipping bar in the drawer summary (snippets/cart-summary.liquid), same wording and maths.
 * @param {Element} inner
 * @param {{ free: number, format: string, currency: string }} cart
 * @param {number} total
 * @param {Undo[]} undo
 */
function paintShipping(inner, cart, total, undo) {
  const message = inner.querySelector('.jh-drawer-ship__msg');
  const fill = /** @type {HTMLElement | null} */ (inner.querySelector('.jh-drawer-ship__fill'));
  if (!message || !fill || cart.free <= 0) return;

  const widthBefore = fill.style.width;
  const childrenBefore = [...message.childNodes];
  fill.style.width = `${Math.min(100, Math.floor((Math.max(0, total) * 100) / cart.free))}%`;
  if (total >= cart.free) {
    const star = document.createElement('span');
    star.setAttribute('aria-hidden', 'true');
    star.textContent = '✦';
    message.replaceChildren(star, ` ${FREE_SHIPPING_MESSAGE}`);
  } else {
    message.replaceChildren(`Add ${formatCents(cart, cart.free - total)} more for free shipping`);
  }
  undo.push(() => {
    fill.style.width = widthBefore;
    message.replaceChildren(...childrenBefore);
  });
}

/**
 * The total is a live region: hold its announcement until the number is final.
 * @param {Element} inner
 * @param {boolean} busy
 * @param {Undo[]} [undo]
 */
function setBusy(inner, busy, undo) {
  const status = inner.querySelector('.cart-totals__total');
  if (!status) return;
  if (busy) {
    status.setAttribute('aria-busy', 'true');
    undo?.push(() => status.removeAttribute('aria-busy'));
  } else {
    status.removeAttribute('aria-busy');
  }
}
